import React from "react";
import {Button, Table, TableBody, TableCell, TableContainer, TableRow, Paper} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import styles from "./InvoiceContent.module.scss";
import GeneralDialog from "../../core/GeneralDialog";
import InputCell from "../../core/InputCell";
import { InvoiceMasterData} from "../../../apis/models/InvoiceMasterRequest";
import { InvoiceContentRequest } from "../../../apis/models/InvoiceContentRequest";
import { formatCurrency, formatNumber, str2number } from "../../../libs/helpers";
import { InvStatus } from "../../../libs/constants";
import InvoiceContentDialog from "../../dialogs/invoiceDetails/InvoiceContentDialog";
import InvoiceDiscountDialog from "../../dialogs/invoiceDetails/InvoiceDiscountDialog";

type ErrorInfo = {
  name?: string;
  quantity?: string;
  unitPrice?: string;
  totalPrice?: string;
}

export type InvoiceContentTabType = InvoiceContentRequest & {
  error: ErrorInfo
}

const InvoiceContent = (props: invoiceContentProps) => {
  const [openWarningDialog, setOpenWarningDialog] = React.useState<boolean>(false);
  const [openAddDialog, setOpenAddDialog] = React.useState<boolean>(false);
  const [openDiscountDialog, setOpenDiscountDialog] = React.useState<boolean>(false);

  const validateRow = (item: InvoiceContentTabType) => {
    const error: ErrorInfo = {};
    error.name = item.name.length <= 0 ? "入力必須です" : "";
    if (isFixRow(item)) {
    } else if (isDiscountRow(item)) {
      error.totalPrice = validateDiscountPrice(item.totalPrice);
    } else {
      error.quantity = validateNumber(item.quantity);
      error.unitPrice = validateNumber(item.unitPrice);
    }
    return error;
  }

  const validateNumber = (value: number | undefined) => {
    if (value === undefined || value === 0) {
      return "入力必須です"
    } else if (isNaN(+value) || value < 1) {
      return "１以上を入力してください"
    }
    return undefined
  }

  const validateDiscountPrice = (value: number) => {
    if (value === 0) {
      return "入力必須です"
    } else if (isNaN(+value) || value >= 1) {
      return "１以下を入力してください"
    }
    return undefined
  }

  const handleChangeQuantity = (item: InvoiceContentTabType, value: string | number, rowIndex: number) => {
    const quantity = str2number(value + "");
    const unitPrice = item.unitPrice ?? 0;
    handleEdit({...item, quantity: quantity, totalPrice: quantity * unitPrice}, rowIndex)
  }

  const handleChangeUnitPrice = (item: InvoiceContentTabType, value: string | number, rowIndex: number) => {
    const quantity = item.quantity ?? 0;
    const unitPrice = str2number(value + "");
    handleEdit({...item, unitPrice: unitPrice, totalPrice: quantity * unitPrice}, rowIndex)
  }

  const handleEdit = (item: InvoiceContentTabType, id: number) => {
    const copyData = [...props.tabValues];
    item.error = validateRow(item);
    copyData.splice(id, 1, item);
    props.onChange(copyData);
  }

  const handleDelete = (rowIndex: number) => {
    props.onChange(props.tabValues.filter((_, index) => index !== rowIndex));
  }

  const handleAddDialogData = (items: InvoiceContentRequest[]) => {
    const addData = items.map(item => {
      return {
        invoiceMasterId: item.invoiceMasterId,
        name: item.name,
        nameAlias: item.nameAlias,
        unitPrice: item.unitPrice,
        quantity: item.quantity,
        totalPrice: item.totalPrice,
        description: item.description,
        order: item.order,
        error: {}
      } as InvoiceContentTabType
    })
    props.onChange(props.tabValues.concat(addData));
  }

  const isFixRow = (rowItem: InvoiceContentTabType): boolean => {
    const fixedNameAliases = ['processFeeAgg', 'officeWorkFeeAgg', 'wrappingFeeAgg', 'logisticFeeAgg', 'shippingItemAgg', 'inspectionFee', 'shippingFee', 'sagawaMoto', 'sagawaChaku', 'yamatoMoto', 'yamatoChaku'];
    return fixedNameAliases.findIndex(alias => alias === rowItem.nameAlias) >= 0;
  }

  const isDiscountRow = (rowItem: InvoiceContentTabType): boolean => {
    return rowItem.quantity === undefined && rowItem.unitPrice === undefined && rowItem.totalPrice < 0;
  }

  const quantityCell = (rowItem: InvoiceContentTabType, rowIndex: number) => {
    if (invoiceData.isConfirmed) {
      return <td className={styles.text_right}>{formatNumber(Number(rowItem.quantity), "-")}</td>;
    } else if (isFixRow(rowItem)) {
      return <td className={`${styles.disabled} ${styles.text_right}`}>{formatNumber(Number(rowItem.quantity))}</td>;
    } else if (isDiscountRow(rowItem)) {
      return <td className={`${styles.disabled} ${styles.text_right}`}>-</td>
    } else {
      return (
        <td style={{padding: "0 6px"}}>
          <InputCell
            type="number"
            textAlignment="right"
            isConfirmed={invoiceData.isConfirmed}
            value={rowItem.quantity ?? ""}
            onChangeText={(val) => handleChangeQuantity(rowItem, val, rowIndex)}
          />
        </td>
      )
    }
  }

  const unitPriceCell = (rowItem: InvoiceContentTabType, rowIndex: number) => {
    if (invoiceData.isConfirmed) {
      return <td className={styles.text_right}>{formatNumber(Number(rowItem.unitPrice), "-")}</td>;
    } else if (isFixRow(rowItem) || isDiscountRow(rowItem)) {
      return <td className={`${styles.disabled} ${styles.text_right}`}>-</td>
    } else {
      return (
        <td style={{padding: "0 6px"}}>
          <InputCell
            type="number"
            textAlignment="right"
            isConfirmed={invoiceData.isConfirmed}
            value={rowItem.unitPrice ?? ""}
            onChangeText={(val) => handleChangeUnitPrice(rowItem, val, rowIndex)}
          />
        </td>
      )
    }
  }

  const totalCell = (rowItem: InvoiceContentTabType, rowIndex: number) => {
    if (invoiceData.isConfirmed && rowItem.nameAlias === "wrappingFeeAgg"){
      return <td className={styles.text_right}>{formatNumber(Number(rowItem.totalPrice), "0")}</td>;
    } else if (invoiceData.isConfirmed) {
      return <td className={styles.text_right}>{formatNumber(Number(rowItem.totalPrice), "-")}</td>;
    } else if (isDiscountRow(rowItem)) {
      return (
        <td style={{padding: "0 6px"}}>
          <InputCell
            type="number"
            textAlignment="right"
            isConfirmed={invoiceData.isConfirmed}
            value={rowItem.totalPrice}
            onChangeText={(val) => handleEdit({...rowItem, totalPrice: str2number(val + "")}, rowIndex)}
          />
        </td>
      )
    } else {
      return <td className={`${styles.disabled} ${styles.text_right}`}>{formatNumber(rowItem.totalPrice)}</td>
    }
  }

  const descriptionCell = (rowItem: InvoiceContentTabType, rowIndex: number) => {
    const colSpan = invoiceData.isConfirmed ? 2 : 1;
      return (
        <td colSpan={colSpan}>
          <InputCell
            type="text"
            textAlignment="left"
            isConfirmed={invoiceData.isConfirmed}
            value={rowItem.description ?? ""}
            onChangeText={(val) => handleEdit({...rowItem, description: val.toString()}, rowIndex)}
          />
        </td>
      )
  }

  const deleteButtonCell = (rowItem: InvoiceContentTabType, rowIndex: number) => {
    if (invoiceData.isConfirmed) {
      return <></>;
    } else if (isFixRow(rowItem)) {
      return <td></td>
    } else {
      return (
        <td>
          <CloseIcon color="primary" className={styles.delete_icon} onClick={() => handleDelete(rowIndex)}/>
        </td>
      )
    }
  }

  const priceData = React.useMemo(() => {
    const withoutTax = props.tabValues.reduce((prev, d) => prev + d.totalPrice, 0);
    const tax = Math.floor(withoutTax * props.taxRate / 100);
    const total = withoutTax + tax;
    return {total, withoutTax, tax}
  }, [props.tabValues, props.taxRate]);

  const invoiceData = React.useMemo(() => {
    const bankInfo = `${props.targetInvoice?.visionBankName ?? ""} ${props.targetInvoice?.visionBranchName ?? ""}`;
    const accountInfo = `${props.targetInvoice?.visionAccountType ?? ""} ${props.targetInvoice?.visionAccountName ?? ""}`;
    const companyInfo = `${props.targetInvoice?.companyName ?? ""}`;
    const brandNo = `No.${props.targetInvoice?.brandNo ?? ""}`;
    const isConfirmed = props.targetInvoice?.status !== InvStatus.NOT_CONFIRMED;
    return {bankInfo, accountInfo, companyInfo, brandNo, isConfirmed}
  }, [props.targetInvoice]);

  const hasError = React.useMemo(() => {
    return props.tabValues.some(fee => {
      return fee.error.name || fee.error.quantity || fee.error.unitPrice || fee.error.totalPrice
    })
  }, [props.tabValues]);

  return (
    <>
      <div className={styles.tablearea}>
        {
        invoiceData.isConfirmed ? 
          <div className={styles.function}>
            <Button className={styles.EditBtn} variant="outlined" onClick={() => setOpenWarningDialog(true)}>編集</Button>
          </div> : <></>
        }
        <div className={styles.table}>
          <div className={styles.LeftTable}>
            <div className={styles.LeftUpTable}>
              <TableContainer component={Paper}>
                <Table aria-label="simple table">
                  <TableBody>
                    <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                      <TableCell component="th" scope="row" sx={{ color: "#1E53A3" }}>請求金額（税込）</TableCell>
                      <TableCell align="right">{formatCurrency(priceData.total)}</TableCell>
                    </TableRow>
                    <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                      <TableCell component="th" scope="row" sx={{ color: "#1E53A3" }}>請求金額（税抜）</TableCell>
                      <TableCell align="right">{formatCurrency(priceData.withoutTax)}</TableCell>
                    </TableRow>
                    <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                      <TableCell component="th" scope="row" sx={{ color: "#1E53A3" }}>消費税額</TableCell>
                      <TableCell align="right">{formatCurrency(priceData.tax)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
            <div className={styles.LeftDownTable}>
              <TableContainer component={Paper}>
                <Table aria-label="simple table">
                  <TableBody>
                      <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 }}}>
                        <TableCell className={styles.LeftDownTableName} component="th" scope="row" sx={{ color: "#1E53A3" }}>振込先</TableCell>
                        <TableCell className={styles.LeftDownTableValue} align="left">
                          {invoiceData.bankInfo}<br />
                          {invoiceData.accountInfo}
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 }}}>
                        <TableCell className={styles.LeftDownTableName} component="th" scope="row" sx={{ color: "#1E53A3" }}>発行先</TableCell>
                        <TableCell className={styles.LeftDownTableValue} align="left">
                          {invoiceData.companyInfo}<br />
                          {invoiceData.brandNo}
                        </TableCell>
                      </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </div>
          <div className={styles.table__wrapper}>
            <table>
              <thead>
                <tr>
                  <td style={{width: "200px"}}></td>
                  <td style={{width: "64px"}}>数量</td>
                  <td style={{width: "64px"}}>単価</td>
                  <td style={{width: "72px"}}>合計金額</td>
                  <td style={{width: "200px"}}>摘要</td>
                  <td style={{width: "24px"}}></td>
                </tr>
              </thead>
              <tbody>
                {
                  props.tabValues.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <InputCell
                          isConfirmed={invoiceData.isConfirmed}
                          onChangeText={(val) => handleEdit({...item, name: val.toString()}, index)}
                          type="text"
                          value={item.name}
                          textAlignment="left"
                        />
                      </td>
                      {quantityCell(item, index)}
                      {unitPriceCell(item, index)}
                      {totalCell(item, index)}
                      {descriptionCell(item, index)}
                      {deleteButtonCell(item, index)}
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
        {
        !invoiceData.isConfirmed ?
          <div className={styles.add_feature}>
            <Button color="primary" variant="outlined" onClick={() => setOpenDiscountDialog(true)}>＋割引項目追加</Button>
            <Button color="primary" variant="outlined" onClick={() => setOpenAddDialog(true)}>＋項目追加</Button>
          </div> : <></>
        }
      </div>
      <GeneralDialog
        open={openWarningDialog}
        onCancel={() => setOpenWarningDialog(false)}
        cancelText="キャンセル"
        onSave={() => {
          props.onNotYetConfirmed();
          setOpenWarningDialog(false);
        }}
        saveText="確定を取り消す"
        title="請求確定取り消し"
        onClose={() => setOpenWarningDialog(false)}
      >
        <div>
          既に確定済の請求です。
          <br />
          内容を編集するには請求の確定取り消しが必要です。
          <br />
          <br />
        </div>
        <div>請求の確定を取り消しますか？</div>
      </GeneralDialog>
      {
      !invoiceData.isConfirmed ?
        <div className={styles.functions}>
          <Button variant="outlined" color="primary" onClick={() => props.onReset()} disabled={!props.isChanged}>編集内容を破棄</Button>
          <Button variant="contained" color="secondary" onClick={() => props.onSave()} disabled={hasError || !props.isChanged}>保存</Button>
        </div> : <></>
      }
      {
        openAddDialog &&
        <InvoiceContentDialog
          onClose={() => setOpenAddDialog(false)}
          open={openAddDialog}
          onSave={(val) => handleAddDialogData(val)}
          targetBrand={props.targetInvoice.brand}
          targetInvoice={props.targetInvoice}
        />
      }
      {
        openDiscountDialog &&
        <InvoiceDiscountDialog
          onClose={() => setOpenDiscountDialog(false)}
          open={openDiscountDialog}
          onSave={(val) => handleAddDialogData(val)}
          targetBrand={props.targetInvoice.brand}
          targetInvoice={props.targetInvoice}
        />
      }
    </>
  );
};

export default InvoiceContent;

export type invoiceContentProps = {
  targetInvoice: InvoiceMasterData;
  tabValues: InvoiceContentTabType[];
  taxRate: number;
  isChanged: boolean;
  onNotYetConfirmed: () => void;
  onReset: () => void;
  onSave: () => void;
  onChange: (items: InvoiceContentTabType[]) => void;
};
